/** Angular Core */
import { Injectable } from '@angular/core';
/** Services */
import { ProspectoService } from '@services/prospecto/prospecto.service';
import { BAService } from '@services/coverages/BA/ba.service';
import { FactoresService } from '@services/factores/factores.service';
import { RecursosExternosService } from '@services/recursosExternos/recursos-externos.service';
/** Rxjs */
import { BehaviorSubject, Subscription } from 'rxjs';
/** Constants */
import { PAE } from '@constants/coverages';
/** Models */
import { Plazo } from '../../../models';
import { OpcionesPlanService } from '@services/opcionesPlan/opciones-plan.service';

/**
 * Servicio para la cobertura PAE
 */
@Injectable({
  providedIn: 'root'
})
export class PAEService {
  /**
   * Crea una instancia reactiva de la prima anual en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_UDI = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_MXN = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de suma asegurada en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  sumaAsegurada_UDI = new BehaviorSubject<number>(0);

  /**
   * Crea una instancia reactiva de suma asegurada en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  sumaAsegurada_MXN = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual sugerida 1 en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_UDI_Sugerida1 = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual sugerida 1 en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_MXN_Sugerida1 = new BehaviorSubject<number>(0);
  // Prima anual sugerida 2
  /**
   * Crea una instancia reactiva de la prima anual sugerida 2 en la primera moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_UDI_Sugerida2 = new BehaviorSubject<number>(0);
  /**
   * Crea una instancia reactiva de la prima anual sugerida 2 en la segunda moneda
   * @property
   * @type {BehaviorSubject<number>}
   * @default 0
   */
  primaAnual_MXN_Sugerida2 = new BehaviorSubject<number>(0);

  // Observables
  /**
   * Crea una instancia reactiva como observable de la prima anual en la primera moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  primaAnual_UDI$ = this.primaAnual_UDI.asObservable();
  /**
   * Crea una instancia reactiva como observable de la prima anual en la segunda moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  primaAnual_MXN$ = this.primaAnual_MXN.asObservable();
  /**
   * Crea una instancia reactiva como observable de la suma asegurada en la primera moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  sumaAsegurada_UDI$ = this.sumaAsegurada_UDI.asObservable();
  /**
   * Crea una instancia reactiva como observable de la suma asegurada en la segunda moneda
   * @property
   * @type {Observable<number>}
   * @default 0
   */
  sumaAsegurada_MXN$ = this.sumaAsegurada_MXN.asObservable();
  /**
   * Selección por defecto
   * @property
   * @default true
   */
  DEFAULT = true;
  /**
   * Crea una suscripción almacenando la suma asegurada de la cobertura Básica
   * @property {Subscription}
   */
  sumaAsegurada_BA: Subscription;

  prima_DiezMasPae = 0;

  /**
   * Constructor
   * @constructor
   * @param recursosExternos Crea instancia de RecursosExternosService
   * @param factores Crea instancia de FactoresService
   * @param BaService Crea instancia de BAService
   * @param prospectoService Crea instancia de ProspectoService
   */
  constructor(
    private recursosExternos: RecursosExternosService,
    private factores: FactoresService,
    private BaService: BAService,
    private prospectoService: ProspectoService,
    private opc: OpcionesPlanService
  ) {
  }
  /**
   * Establece la prima anual en la cobertura dado el valor
   * @param sumaAseguradaUDI
   */
  establecePrimaAnual(sumaAseguradaUDI) {
    this.sumaAsegurada_BA = this.BaService.sumaAsegurada_UDI.distinctUntilChanged()
    .subscribe(() => {
      this.obtenerPrima(sumaAseguradaUDI);
    });
  }
  /**
   * Obtiene la prima apartir del valor asignado
   * @param {number} sumaAsegurada
   */
  obtenerPrima(sumaAsegurada: number): void {
    // Variable de suma asegurada que contendra el valor final de la suma asegurada despues de ser evaluada.
    let suma = 0;

    /**
     * Si la suma asegurada inicial es igual a 0, tomará por defecto el valor de la suma asegurada basica
     * de lo contrario continuará evaluando la suma asegurada introducida.
    */
    this.DEFAULT = sumaAsegurada === 0;

    // Se obtienen la informacion del prospecto actual
    const prospecto = this.prospectoService.prospecto.value;

    // Evalua si la edad del prospecto esta dentro del rango de aceptación de la cobertura
    if (
      prospecto.age >= PAE.ACCEPTANCE_AGE.MIN &&
      prospecto.age <= PAE.ACCEPTANCE_AGE.MAX
    ) {

      if (sumaAsegurada !== -1) {
        if (this.DEFAULT) {
          suma = this.BaService.sumaAsegurada_UDI.value;
          this.DEFAULT = false;
        } else {
          suma = sumaAsegurada || 0;
        }

        if (
          suma > this.BaService.sumaAsegurada_UDI.value ||
          suma < this.recursosExternos.GetMinMax('BA', 'MIN')
        ) {
          suma = this.BaService.sumaAsegurada_UDI.value;
        }
        if (this.opc.tipoPlan === 'TEMPORAL' && this.opc.moneda === 'MXN') {
          suma = this.recursosExternos.obtenerSumaAsegurada('PlanTmpPAE', suma);
        } else {
          suma = this.recursosExternos.obtenerSumaAsegurada('PAE', suma);
        }

        // if (suma <= this.recursosExternos.GetMinMax('PAE', 'MIN')) {
        //   suma = this.recursosExternos.GetMinMax('PAE', 'MIN');
        // }

        // if (suma >= this.recursosExternos.GetMinMax('PAE', 'MAX')) {
        //   suma = this.recursosExternos.GetMinMax('PAE', 'MAX');
        // }

        const primaPAE_UDI = this.calculoPrimaPAE(this.recursosExternos.FirstToUDI(suma), 'Elegido');
        const primaPAE_MXN = this.recursosExternos.UDIToSecond(primaPAE_UDI);

        const primaPAE_UDI_Sugerida1 = this.calculoPrimaPAE(this.recursosExternos.FirstToUDI(suma), 'Sugerido1');
        const primaPAE_MXN_Sugerida1 = this.recursosExternos.UDIToSecond(primaPAE_UDI_Sugerida1);

        const primaPAE_UDI_Sugerida2 = this.calculoPrimaPAE(this.recursosExternos.FirstToUDI(suma), 'Sugerido2');
        const primaPAE_MXN_Sugerida2 = this.recursosExternos.UDIToSecond(primaPAE_UDI_Sugerida2);

        const sumaAsegurada_UDI = suma;
        const sumaAsegurada_MXN = this.recursosExternos.FirstToSecond(sumaAsegurada_UDI);

        this.primaAnual_UDI.next(this.recursosExternos.UDIToFirst(primaPAE_UDI));
        this.primaAnual_MXN.next(primaPAE_MXN);

        this.primaAnual_UDI_Sugerida1.next(this.recursosExternos.UDIToFirst(primaPAE_UDI_Sugerida1));
        this.primaAnual_MXN_Sugerida1.next(primaPAE_MXN_Sugerida1);

        this.primaAnual_UDI_Sugerida2.next(this.recursosExternos.UDIToFirst(primaPAE_UDI_Sugerida2));
        this.primaAnual_MXN_Sugerida2.next(primaPAE_MXN_Sugerida2);

        this.sumaAsegurada_UDI.next(sumaAsegurada_UDI);
        this.sumaAsegurada_MXN.next(sumaAsegurada_MXN);

        return;

      }

    }
      this.resetValues();
      return;
  }

  /**
   * Realiza el calculo de la tarifa
   *
   * @param {number} calculoPrimaPAE
   * @returns {number} Prima PAE
   * @memberof PAEService
   */
  calculoPrimaPAE(sumaAsegurada: number, plazo: Plazo, isINPC: boolean = false, edadInpc?: number): number {
    // Variables necesarias para el calculo
    const factorPAE = this.factores.obtenerFactorCobertura('PAE', plazo, isINPC, edadInpc);
    // Ejecuta la formula dada

    const primaPAE = this.obtenerPrimaPae(sumaAsegurada, factorPAE);
    // Retorna el resultado de la operación
    return primaPAE;
  }

      /**
* MÉTODO QUE CALCULA LA NUEVA PRIMA CADA 10 ANIOS
* @param {number} edad
* @memberof PAEService
*/
calcularPrimaDiezMas(edad: number) {
  let primaPAE = 0;
  if (this.opc.coberturaEnVigencia(this.primaAnual_UDI.value, PAE, edad) > 0) {
    const factorPae = this.factores.obtenerFactoresDiezMas('PAE', edad);
    primaPAE = this.obtenerPrimaPae(this.sumaAsegurada_UDI.value, factorPae);
    primaPAE = primaPAE - this.primaAnual_UDI.value;
  }
  this.prima_DiezMasPae = primaPAE;
}

obtenerPrimaPae(sumaAsegurada: number, factorPAE: number){
  const primaPAE = factorPAE !== 0 ? (
    sumaAsegurada * factorPAE / 1000
  ) : 0;
  // Retorna el resultado de la operación
  return primaPAE;
}

  resetValues() {
    this.primaAnual_UDI.next(0);
    this.primaAnual_MXN.next(0);
    this.primaAnual_UDI_Sugerida1.next(0);
    this.primaAnual_MXN_Sugerida1.next(0);
    this.primaAnual_UDI_Sugerida2.next(0);
    this.primaAnual_MXN_Sugerida2.next(0);
    this.sumaAsegurada_UDI.next(0);
    this.sumaAsegurada_MXN.next(0);
    this.DEFAULT = true;
  }
}
